import { useState } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../../../../context/I18nContext';
import { useModal } from '../../../../../context/ModalContext';
import { useLoading } from '../../../../../context/LoadingContext';
import { getCurrentLocation, getLocationErrorMessage, formatLocationForSharing } from '../../../../../services/locationService';
import logger from '../../../../../utils/logger';
import './AttachmentModal.css';

const AttachmentModalItem = ({
  icon = null,
  label = 'placeholder',
  onClick = () => {},
}) => (
  <div className="attachment-modal-item" onClick={onClick} title={label}>
    <div className="attachment-modal-item-icon">{icon}</div>
    <div className="attachment-modal-item-label">{label}</div>
  </div>
);

AttachmentModalItem.propTypes = {
  icon: PropTypes.element,
  label: PropTypes.string,
  onClick: PropTypes.func,
};

const AttachmentModal = ({
  onAttachFileClick = () => {},
  onShareCurrentLocation = () => {},
  onClose = () => {},
  replyingTo = null,
}) => {
  const { t } = useI18n();
  const { showError } = useModal();
  const { showLoading, hideLoading } = useLoading();
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  /**
   * Handle sharing current location
   * Gets user's current position via Geolocation API
   * Includes 30-second timeout and loading state management
   */
  const handleShareCurrentLocation = async () => {
    // Prevent multiple clicks while loading
    if (isLoadingLocation) {
      logger.debug('[AttachmentModal] Location request already in progress');
      return;
    }
    
    logger.debug('[AttachmentModal] Requesting current location');
    
    // Close the attachment modal immediately
    onClose();
    
    // Wait a brief moment for the modal to close before showing loading spinner
    await new Promise(resolve => setTimeout(resolve, 150));
    
    // Set loading state and show loading spinner
    setIsLoadingLocation(true);
    showLoading(t('ChatView.loadingLocationMessage'));
    
    // Create a 30-second timeout promise
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject('TIMEOUT'), 30000),
    );
    
    try {
      const locationPromise = getCurrentLocation();
      const locationData = await Promise.race([locationPromise, timeoutPromise]);
      const locationUrl = formatLocationForSharing(locationData);
      
      logger.debug('[AttachmentModal] Location retrieved successfully, calling callback');
      
      // Hide loading spinner and reset state
      hideLoading();
      setIsLoadingLocation(false);
      
      onShareCurrentLocation(locationData, locationUrl);
    } catch (errorType) {
      // If high accuracy fails with POSITION_UNAVAILABLE, retry with lower accuracy
      if (errorType === 'POSITION_UNAVAILABLE') {
        logger.debug('[AttachmentModal] High accuracy failed, retrying with lower accuracy');
        // Keep loading indicator visible during fallback
        try {
          // Create new timeout for fallback request
          const fallbackTimeout = new Promise((_, reject) => 
            setTimeout(() => reject('TIMEOUT'), 15000),
          );
          const fallbackPromise = getCurrentLocation({ enableHighAccuracy: false, timeout: 15000 });
          const locationData = await Promise.race([fallbackPromise, fallbackTimeout]);
          const locationUrl = formatLocationForSharing(locationData);
          
          // Hide loading after fallback succeeds
          hideLoading();
          setIsLoadingLocation(false);
          logger.debug('[AttachmentModal] Location retrieved with lower accuracy');
          onShareCurrentLocation(locationData, locationUrl);
          return;
        } catch (fallbackError) {
          // Hide loading after fallback fails
          hideLoading();
          setIsLoadingLocation(false);
          logger.warn('[AttachmentModal] Fallback location request also failed:', fallbackError);
          const { title, message } = getLocationErrorMessage(fallbackError, t);
          showError(title, message);
          return;
        }
      }
      
      // For other errors, hide loading immediately
      hideLoading();
      setIsLoadingLocation(false);
      logger.warn('[AttachmentModal] Location error:', errorType);
      const { title, message } = getLocationErrorMessage(errorType, t);
      showError(title, message);
    }
  };

  const replyLabel = replyingTo
    ? t(
      'MessageInput.replyingTo',
      { name: replyingTo.senderName || t('MessageInput.unknownUser', {}, 'Unknown') },
      `Replying to ${replyingTo.senderName || 'Unknown'}`,
    )
    : null;

  return (
    <div className="attachment-modal">
      {replyingTo && (
        <div className="attachment-modal-reply-indicator">
          <span className="material-icons" aria-hidden="true">reply</span>
          <span className="attachment-modal-reply-text">{replyLabel}</span>
        </div>
      )}
      <AttachmentModalItem
        icon={<ion-icon name="document" />}
        label={t('ChatOptionsView.fromDeviceTitle')}
        onClick={onAttachFileClick}
      />
      <AttachmentModalItem
        icon={<ion-icon name="location" />}
        label={t('ChatOptionsView.shareLocationTitle')}
        onClick={handleShareCurrentLocation}
      />
    </div>
  );
};

AttachmentModal.propTypes = {
  onAttachFileClick: PropTypes.func,
  onShareCurrentLocation: PropTypes.func,
  onClose: PropTypes.func,
  replyingTo: PropTypes.shape({
    id: PropTypes.string,
    senderName: PropTypes.string,
    content: PropTypes.string,
  }),
};

export default AttachmentModal;
